import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Paper = styled.div`
  background: ${props => props.theme === 'dark' ? '#1a1a1a' : '#ffffff'};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  color: ${props => props.theme === 'dark' ? '#ffffff' : '#000000'};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1rem;
  color: ${props => props.theme === 'dark' ? '#ffffff' : '#000000'};
`;

const Subtitle = styled.p`
  font-size: 1rem;
  text-align: center;
  color: ${props => props.theme === 'dark' ? '#a0aec0' : '#4a5568'};
  margin-bottom: 2rem;
`;

const Introduction = styled.div`
  margin: 2rem 0;
  line-height: 1.6;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${props => props.theme === 'dark' ? '#ffffff' : '#000000'};
`;

const SectionContent = styled.div`
  line-height: 1.6;
  white-space: pre-line;
  
  ul {
    padding-left: 1.5rem;
    margin: 1rem 0;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${props => props.theme === 'dark' ? '#2d3748' : '#e2e8f0'};
  margin: 2rem 0;
`;

const PrivacyPolicy = ({ policyData }) => {
  if (!policyData) {
    return null;
  }

  // Get theme from localStorage or default to 'light'
  const theme = localStorage.getItem('theme') || 'light';
  const {
    title,
    effectiveDate,
    introduction,
    sections = [],
  } = policyData || {};
  const formattedDate = effectiveDate
    ? new Date(effectiveDate).toLocaleDateString()
    : null;

  return (
    <Container>
      <Paper theme={theme}>
        {title && <Title theme={theme}>{title}</Title>}

        {formattedDate && (
          <Subtitle theme={theme}>
            Effective Date: {formattedDate}
          </Subtitle>
        )}

        {introduction && (
          <Introduction>
            {introduction}
          </Introduction>
        )}

        {!!sections.length && <Divider theme={theme} />}

        {sections.map((section, index) => {
          const sectionTitle = section?.title;
          const sectionContent = section?.content;

          if (!sectionTitle && !sectionContent) {
            return null;
          }

          return (
            <Section key={index}>
              {sectionTitle && (
                <SectionTitle theme={theme}>
                  {index + 1}. {sectionTitle}
                </SectionTitle>
              )}
              {sectionContent && (
                <SectionContent>
                  {sectionContent}
                </SectionContent>
              )}
              {index < sections.length - 1 && <Divider theme={theme} />}
            </Section>
          );
        })}
      </Paper>
    </Container>
  );
};

PrivacyPolicy.propTypes = {
  policyData: PropTypes.shape({
    title: PropTypes.string,
    effectiveDate: PropTypes.string,
    introduction: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        content: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default PrivacyPolicy; 
